import axios, { AxiosRequestConfig } from "axios";

import { PaginationParams, AccountParams, SecureParams, CustomWindow } from "types/common";
import { getStorage, setStorage } from "utils/storage";
import { getNewToken } from "./token";
import profileLogout from "@/pages/Profile/utils/logout";
import deepTrim from "./utils";
import { notification } from "antd";
import i18next from "i18next";

export const BASEURL = (window as unknown as CustomWindow).config.server_url;
export const VERSION = (window as unknown as CustomWindow).config.version;

export const requestFreshToken = axios.create({
  baseURL: BASEURL,
  headers: {
    Accept: "application/json, text/plain, */*",
    "Content-Type": "application/json",
    "g_wms_version": VERSION,
    "g_wms_app_type": "WEB"
  },
});

// 创建实例
const http = axios.create({
  baseURL: BASEURL,
  timeout: 300000,
  headers: {
    Accept: "application/json, text/plain, */*",
    Authorization: "",
    "g_wms_version": VERSION,
    "g_wms_app_type": "WEB"
  },
});

//配置发送请求前的拦截器 可以设置token信息
http.interceptors.request.use(
  (config) => {
    const isSupplierPage = getStorage("SUPPLIER_ID")
    if (isSupplierPage) {
      config.headers["supplier_id"] = isSupplierPage;
      config.headers["Authorization"] = getStorage("SUPPLIER_ACCESS_TOKEN");
    } else if (config.url !== "/wms/user/login") {
      config.headers["Authorization"] = getStorage("accessToken");
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

interface AxiosResponse<Params> {
  data: { code: number; body: Params; pagination?: PaginationParams };
  status: number;
  statusText: string;
  headers: unknown;
  config: AxiosRequestConfig;
  request?: unknown;
}
const ERR_BODY_CODE: number[] = [
  70805, 70806, 70807, 70704, 70809, 70703,
  70605, 73007, 73008, 73024, 73028, 73010, 70873, 73011, 74308,
  // 70845
];
// 配置响应拦截器
http.interceptors.response.use(
  (res) => {
    return new Promise(async (resolve, reject) => {
      if (res.data.code === 200) {
        if (res.config.url === '/wms/supplier/user/login' && res.headers["x-supplier-id"]) {
          setStorage("SUPPLIER_ID", res.headers["x-supplier-id"]);
        }
        resolve(res);
      } else {
        // 69107 ---> 缺少token, 请检查 header
        const renewCode = [69017, 69018];
        if (renewCode.includes(res.data.code)) {
          // 未配置
          await getNewToken().then((accessTk) => {
            res.config.headers.Authorization = accessTk;
            return http(res.config).then((newRes) => resolve(newRes));
          });
        } else if (res.data.code === 70203) {
          profileLogout()
        }
        else if (ERR_BODY_CODE.includes(res.data.code)) {
          reject(res.data)
        } else if (res.data.code === 69019) {
          notification.error({
            message: i18next.t('common.versionFalse'),
            duration: 0
          })
          // setTimeout(() => {
          //   window.location.reload();
          // }, 1500);
          reject(res.data.code);
        }
        reject(res.data.code);
      }
    });
  },
  (error) => {
    // ✅ 处理取消的请求
    if (error.name === 'AbortError' || error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      console.log(error);

      return Promise.reject({ name: 'AbortError', message: 'Request canceled' });
    }
    if (error.code === "ERR_NETWORK") {
      // 网络错误 -> 10010
      return Promise.reject(10010);
    } else if (error.code === "ECONNABORTED") {
      // 请求超时
      return Promise.reject(10011);
    } else {
      return Promise.reject(error.code);
    }
  }
);

export interface AxiosResponseBody<Params> {
  body: Params;
  pagination?: PaginationParams;
  code?: number;
}
export const request = <Result, Params = undefined, F = PaginationParams>(
  url: string,
  config?: {
    params?:
    | {
      body?: Params;
      pagination?: F;
      account?: AccountParams;
      secure?: SecureParams;
      ints?: Array<number>;
      strs?: Array<string>;
      controller?: AbortController;  // ✅ 支持 controller 在 params 对象中
    }
    | FormData;
    method?: "post" | "get";
    requestConfig?: AxiosRequestConfig;
    controller?: AbortController;  // 支持取消请求的控制器（顶层）
  }
): Promise<AxiosResponseBody<Result>> => {
  config = config ?? {
    params: undefined,
    method: "post",
    requestConfig: undefined,
  };
  const {
    params = undefined,
    method = "post",
    requestConfig = undefined,
    controller: topController,  // 顶层的 controller
  } = config;

  // ✅ 从 params 对象中提取 controller（如果存在）
  const controller = topController || (params && typeof params === 'object' && 'controller' in params ? params.controller : undefined);

  // ✅ 从 params 中移除 controller，避免传递给服务器
  if (params && typeof params === 'object' && 'controller' in params) {
    delete (params as any).controller;
  }

  const reqConfig: AxiosRequestConfig = {
    baseURL: BASEURL,
    ...requestConfig,
  };

  // 如果提供了 controller，则取其 signal 传递给 axios
  if (controller) {
    reqConfig.signal = controller.signal;
  }

  return new Promise<AxiosResponseBody<Result>>((resolve, reject) => {
    let data = params;  //文件操作传递二进制文件，不可进行此操作
    if (!url.includes('/files') && !url.includes('import')) data = deepTrim(params);

    http(url, { method, ...reqConfig, data })
      .then((response: AxiosResponse<Result>) =>
        resolve({
          body: response.data.body,
          pagination: response.data.pagination,
          code: response.data.code,
        })
      )
      .catch((error: any) => {
        // 处理取消的请求
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
          return reject({ name: 'AbortError', message: 'Request canceled' });
        }
        reject(error)
      });
  });
};

export default request;
